import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import HCaptcha from '@hcaptcha/react-hcaptcha';

import { toast } from 'react-toastify';

import { useLocation, useNavigate, Link } from 'react-router-dom';

import { ArrowLeft } from '../components/icons/arrow-left';
import Input from '../components/common/input';
import Checkbox from '../components/common/checkbox';
import ConfirmAuth from '../components/confirm-auth';

import { useAppSelector } from '../hooks/redux';
import { selectUser } from '../store/auth.slice';
import { useRegistrationMutation } from '../services/auth.api';
import { LANDING } from '../configs/api/api.config';

const schema = yup.object({
  email: yup.string().email('Неверный формат Email').required('Введите Email'),
  referalCode: yup.string().optional(),
  haveEducation: yup.boolean().oneOf([true], 'Подтвердите наличие образования').required(),
  agreePrivacy: yup.boolean().oneOf([true], 'Примите условия соглашения').required(),
});

type FormData = yup.InferType<typeof schema>;

const Registration = () => {
  const [captcha, setCaptcha] = useState('');
  const [resetCaptcha, setResetCaptcha] = useState(0);
  const [isInputAuthCode, setIsInputAuthCode] = useState(false);

  const location = useLocation();

  const { handleSubmit, setError, watch, control, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      haveEducation: false,
      agreePrivacy: false,
      referalCode: '',
    },
    mode: 'onBlur',
  });
  const email = watch('email');
  const [registration] = useRegistrationMutation();
  const userData = useAppSelector(selectUser);
  const navigate = useNavigate();

  useEffect(() => {
    // Get promoCode from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const promoCode = searchParams.get('promoCode');

    // Set the referalCode field's initial value if promoCode exists
    if (promoCode) {
      reset({
        email: '',
        haveEducation: false,
        agreePrivacy: false,
        referalCode: promoCode,
      });
    }

    if (userData) {
      navigate('/consultations', {
        state: { previousLocationPathname: location.pathname },
      });
    }
  }, [userData, navigate, location.pathname, location.search, reset]);

  const onSubmit = async (data: FormData) => {
    if (!captcha) {
      toast.error('Пройдите капчу');

      return;
    }

    try {
      const res = await registration({
        captcha,
        email: data.email,
        referalCode: data.referalCode,
        agree_to_receive_emails: false,
        have_education: data.haveEducation,
      }).unwrap();

      if (res) {
        toast.success('Вы успешно зарегистрировались');
        setIsInputAuthCode(true);
      }
    } catch (e: any) {
      if (e.status === 422 && e.data?.detail) {
        setError('email', { message: e.data.detail });
      } else {
        toast.error('Произошла ошибка');
      }
    } finally {
      setResetCaptcha((prev) => prev + 1);
    }
  };

  return (
    <div className="flex min-h-[calc(100dvh-50px)] w-screen items-center justify-center px-[15px] lg:min-h-[calc(100dvh-80px)]">
      {!isInputAuthCode ? (
        <div className="w-[420px] sm:w-[550px] max-h-[90%] overflow-y-auto rounded-[30px] bg-white p-[10px] md:p-[50px] small:p-[10px]">
          <div className="flex items-center font-comfortaa font-bold text-center text-2xl md:text-4xl small:text-2xl mb-2.5">
            <Link to="/" className="group">
              <ArrowLeft className="w-[14px] h-[18px] md:w-[21px] md:h-[27px] fill-blackBase group-hover:fill-redBase" />
            </Link>
            <span className="grow text-center">Регистрация</span>
          </div>

          <div className="text-center sm:px-[30px] small:px-[0px] text-sm md:text-base text-greyBase mb-5 small:mb-3 md:mb-10">
            Зарегистрируйтесь, чтобы получить доступ к личному кабинету
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[12px] mb-4">
            <div>
              <div className="text-xs text-greyBase mb-1">Email</div>
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState: { error } }) => (
                  <Input
                    classNameWrap=""
                    placeholder="Введите Email"
                    error={error?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </div>
            <div>
              <div className="text-xs text-greyBase mb-1">Пригласительный ключ (если есть)</div>
              <Controller
                control={control}
                name="referalCode"
                render={({ field }) => (
                  <Input classNameWrap="" placeholder="Введите ключ" value={field.value} onChange={field.onChange} />
                )}
                defaultValue=""
              />
            </div>

            <div className="flex flex-col gap-[16px] mt-4 mb-4">
              <Controller
                control={control}
                name="haveEducation"
                render={({ field, fieldState: { error } }) => (
                  <Checkbox
                    label="Я подтверждаю, что у меня есть высшее психологическое образование"
                    checked={field.value}
                    onChange={field.onChange}
                    error={!!error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="agreePrivacy"
                render={({ field, fieldState: { error } }) => (
                  <Checkbox
                    label={
                      <p>
                        Я принимаю условия{' '}
                        <Link
                          to={`${LANDING}/wp-content/uploads/2025/03/polzovatelskoe_soglashenie_logosterapi_rev.pdf`}
                          target="_blank"
                          className="underline"
                        >
                          Пользовательского соглашения
                        </Link>{' '}
                        и даю{' '}
                        <Link
                          to={`${LANDING}/wp-content/uploads/2025/03/politika_konfidenczialnosti_logosterapi_rev.pdf`}
                          target="_blank"
                          className="underline"
                        >
                          согласие на обработку персональных данных
                        </Link>
                      </p>
                    }
                    checked={field.value}
                    onChange={field.onChange}
                    error={!!error?.message}
                  />
                )}
              />
            </div>

            <div className="mb-[30px]">
              <HCaptcha
                sitekey="92e461fa-ac46-44ce-8a04-d85a6b185f9e"
                onVerify={(token) => setCaptcha(token)}
                key={resetCaptcha}
                onExpire={() => {
                  setCaptcha('');
                  setResetCaptcha((prev) => prev + 1);
                }}
              />
            </div>

            <button
              className="font-comfortaa font-bold flex justify-center p-[15px] bg-redBase text-white md:text-xl w-full rounded-[10px] hover:bg-redBaseWithBlur20"
              type="submit"
            >
              Зарегистрироваться
            </button>
          </form>

          <div className="text-center">
            <div className="text-sm md:text-base text-greyBase">Есть аккаунт?</div>
            <a href="/" className="text-sm md:text-base text-redBase underline">
              Войти
            </a>
          </div>
        </div>
      ) : (
        <ConfirmAuth onBackToAuth={() => setIsInputAuthCode(false)} email={email} confirmCodex={true} />
      )}
    </div>
  );
};

export default Registration;
